import { Box, CircularProgress, DialogContentText, Grid, Slider } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Papa from "papaparse"
import { useState } from "react"
import userService from "services/userService"
import type { IUserReport, IUserReportCSV } from "types/reportTypes"
import TimeSpan from "types/timeSpan"

type Props = {
  userId: string
  open: boolean
  handleClose: () => void
}

export function UserReportDialog({ userId, open, handleClose }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [reportPeriod, setReportPeriod] = useState<number>(30)

  const sliderValues = [
    {
      value: 7,
      label: "30 days",
      period: 30,
    },
    {
      value: 20,
      label: "90 days",
      period: 90,
    },
    {
      value: 40,
      label: "180 days",
      period: 180,
    },
    {
      value: 70,
      label: "1 year",
      period: 360,
    },
    {
      value: 100,
      label: "3 years",
      period: 1080,
    },
  ]

  const sliderOnChange = (_event: Event, value: number | number[], _activeThumb: number) => {
    const period = sliderValues.find((x) => x.value === value)

    if (period === undefined) {
      setReportPeriod(30)
    } else {
      setReportPeriod(period.period)
    }
  }

  const getUserReport = () => {
    if (userId) {
      setIsLoading(true)
      userService
        .getUserReport(userId, reportPeriod)
        .then((userReport) => {
          setIsLoading(false)
          downloadReport(userReport)
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    }
  }

  const downloadReport = (userReport: IUserReport) => {
    const csvReport: IUserReportCSV[] = []

    const totalTrainingTime = new TimeSpan(userReport.totalTrainingTimeInSeconds * 1000)

    for (let i = -1; i < userReport.trainingReports.length; i++) {
      if (i === -1) {
        const report: IUserReportCSV = {
          Firstname: userReport.firstname,
          Lastname: userReport.lastname,
          UserGroup: userReport.userGroup,
          PIN: userReport.pin,
          UserCreated: new Date(userReport.createdDate).toLocaleDateString(),
          ActiveLicense: userReport.activeLicense ? "TRUE" : "FALSE",
          TotalTrainingTime: totalTrainingTime.Format(),
          LastActiveDate: new Date(userReport.lastActiveDate).toLocaleString(),
          CompletedLessons: userReport.completedLessons.toString(),
          CompletedDistinctLessons: userReport.completedDistinctLessons.toString(),
          ExamsTaken: userReport.examsTaken.toString(),
          ExamsPassed: userReport.examsPassed.toString(),
          LessonDate: "",
          LessonType: "",
          LessonName: "",
          LessonDuration: "",
          LessonEquipment: "",
          LessonAccessory: "",
          ExamPassed: "",
        }
        csvReport.push(report)
      } else {
        const lessonDuration = new TimeSpan(
          userReport.trainingReports[i].lessonDurationInSeconds * 1000,
        )
        console.log()
        const report: IUserReportCSV = {
          Firstname: "",
          Lastname: "",
          UserGroup: "",
          PIN: "",
          UserCreated: "",
          ActiveLicense: "",
          TotalTrainingTime: "",
          LastActiveDate: "",
          CompletedLessons: "",
          CompletedDistinctLessons: "",
          ExamsTaken: "",
          ExamsPassed: "",
          LessonDate: new Date(userReport.trainingReports[i].lessonDate).toLocaleString(),
          LessonType: userReport.trainingReports[i].lessonType,
          LessonName: userReport.trainingReports[i].lessonName,
          LessonDuration: lessonDuration.Format(),
          LessonEquipment: userReport.trainingReports[i].lessonEquipment,
          LessonAccessory: userReport.trainingReports[i].lessonAccessory,
          ExamPassed: userReport.trainingReports[i].examPassed,
        }
        csvReport.push(report)
      }
    }

    var reportName = `${userReport.lastname}_hiskill_report_${reportPeriod}.csv`

    var csv = Papa.unparse(csvReport)
    var csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    var csvURL = window.URL.createObjectURL(csvData)

    var tempLink = document.createElement("a")
    tempLink.href = csvURL
    tempLink.setAttribute("download", reportName)
    tempLink.click()
  }

  return (
    <div>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{"User report"}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText paddingX={2}>
            Choose the desired reporting period. Reports are updated every six hours for the latest
            information.
          </DialogContentText>
          <Grid paddingY={2} paddingX={2}>
            <Slider
              aria-label="Custom marks"
              defaultValue={7}
              step={null}
              valueLabelDisplay="off"
              marks={sliderValues}
              onChange={sliderOnChange}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Box sx={{ m: 1, position: "relative" }}>
            <Button variant="contained" disabled={isLoading} onClick={getUserReport}>
              Generate
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  )
}
